import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BACKGROUND_OPTIONS, DEFAULT_SELLER_SETTINGS, normalizeBackgroundColor } from "@makit/shared";

import { PageHead } from "../app/PageHead";
import { LimitBanner, ScopeBanner } from "../components/banners";
import { SettingRow } from "../components/settings";
import {
  ACCOUNT_SETTINGS_QUERY_KEY,
  fetchAccountSettings,
  isAccountSettingsWriteOff,
  saveDefaultBackground,
  type AccountSettings,
} from "../lib/account-api";

/** 고를 수 있는 배경. 팔레트의 정본은 shared 의 `BACKGROUND_OPTIONS` 이고(`W-30` 과 같은 원천),
 *  **값(`hex`)이 정해진 색만** 고르게 둔다. ⛔ 값이 없는 색(아이보리 · 쿨그레이, PRD `7.13` D-2)을
 *  `미정` 배지로 세우지 않는다 (2026-10-04 사용자 지시 · design-manager 권고). `W-30` 은 같은 색을
 *  비활성으로 그리지만, 기본값 목록에는 고를 수 있는 것만 둔다. */
const CHOICES = BACKGROUND_OPTIONS.filter(
  (option): option is (typeof BACKGROUND_OPTIONS)[number] & { hex: string } => option.hex !== null,
);

/** `W-80` 환경설정. **계정 메뉴의 `환경설정 ›`도 갈 곳이 없었다** — `W-55` 와 같은 종류의
 * 구멍이다(PRD 7.7.8a).
 *
 * **앱에 있는 스위치 둘이 여기 없고, 대체 스위치를 지어내지 않았다.** 기능 동등은 "같은 일을
 * 할 수 있다"는 뜻이지 "같은 스위치가 있다"는 뜻이 아니다. 짝을 맞추려고 뜻 없는 스위치를
 * 두면 셀러가 눌러 보고 아무 일도 일어나지 않는 것을 겪는다.
 *
 * ⛔ **설계 메모를 걷었다** (2026-10-04 사용자 지시). 예시 표지, 「대체 스위치를 만들지
 * 않았습니다」, 알림 설정 콜아웃(벨도 함께 걷혀 그 말이 거짓이 됐다), 「이 화면에서 아직
 * 못 합니다」, 목적지가 정해지지 않은 「도움」 섹션(`D-32`. 연락처는 맨 아래 사업자 정보에
 * 있다)은 셀러가 아니라 설계자에게 하는 말이었다.
 *
 * **기본 배경은 서버가 쓰는 배포에서만 계정에 저장된다**(`PATCH /api/auth/account/settings`). 목업이 「설정은
 * 계정 단위라 앱에서 한 번 정하면 웹에도 적용됩니다」라고 정했으므로 브라우저에 따로 담지 않는다. 그 배포에서는
 * 앱 `A-40c` 에서 고른 색이 여기 그대로 보이고, 여기서 고른 색이 앱으로 간다.
 * ⚠️ 운영은 처리방침 개정 시행일까지 서버에 쓰지 않는다(사용자 결정 2026-10-28 (다)안 · 법무 `L-C38`). 서버가
 * `savesToAccount: false` 로 말하면 #245 이전처럼 고른 색을 화면 상태로만 들고 그 사실을 하단 문장이 말한다. */
export function SettingsScreen() {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ACCOUNT_SETTINGS_QUERY_KEY, queryFn: fetchAccountSettings });
  /** 서버가 계정 설정을 쓰는 배포인가. 못 읽었거나 끊겼으면 아니다(화면 상태 갈래). */
  const accountMode = settings.data?.savesToAccount === true;
  /** 화면 상태 갈래의 선택. #245 이전처럼 새로고침하면 처음 값(첫 색)으로 돌아간다. */
  const [localBg, setLocalBg] = useState(0);
  /** 고르는 즉시 바꿔 보이고(낙관적), 실패하면 **고르기 전 색으로 되돌린다.** 되돌아간 선택이
   *  실패를 말하는 표시다. 실패 문장은 목업에 없어 짓지 않았다(design-manager 소관).
   *  ⭐ `409 ACCOUNT_SETTINGS_WRITE_OFF`(스위치가 바뀐 사이)는 실패가 아니다. 고른 색을 화면 상태로 두고 캐시를
   *  꺼짐으로 돌린다. */
  const save = useMutation({
    mutationFn: saveDefaultBackground,
    onMutate: async (hex: string) => {
      await queryClient.cancelQueries({ queryKey: ACCOUNT_SETTINGS_QUERY_KEY });
      // ⛔ 못 읽은 값을 지어내 캐시에 넣지 않는다. 고르기는 첫 조회가 끝나기 전에 잠겨 있다.
      const previous = queryClient.getQueryData<AccountSettings>(ACCOUNT_SETTINGS_QUERY_KEY);
      if (previous) {
        queryClient.setQueryData<AccountSettings>(ACCOUNT_SETTINGS_QUERY_KEY, { ...previous, defaultBackgroundColor: hex });
      }
      return { previous };
    },
    onError: (error, hex, context) => {
      if (isAccountSettingsWriteOff(error)) {
        const i = CHOICES.findIndex((option) => option.hex.toUpperCase() === hex);
        if (i !== -1) setLocalBg(i);
        if (context?.previous) {
          queryClient.setQueryData<AccountSettings>(ACCOUNT_SETTINGS_QUERY_KEY, { ...context.previous, savesToAccount: false });
        }
        return;
      }
      if (context?.previous) queryClient.setQueryData<AccountSettings>(ACCOUNT_SETTINGS_QUERY_KEY, context.previous);
    },
    onSuccess: (saved) => {
      queryClient.setQueryData<AccountSettings>(ACCOUNT_SETTINGS_QUERY_KEY, (old) =>
        old ? { ...old, defaultBackgroundColor: saved.defaultBackgroundColor } : old,
      );
    },
  });

  // 계정 갈래: 계정 값을 shared 팔레트로 거른다. 화면 상태 갈래: 고른 자리 그대로.
  // 첫 조회가 끝나기 전에는 어느 갈래인지 몰라 아무 색도 고른 것으로 그리지 않는다.
  const currentHex =
    accountMode && settings.data
      ? (normalizeBackgroundColor(settings.data.defaultBackgroundColor) ?? DEFAULT_SELLER_SETTINGS.defaultBackgroundColor)
      : null;
  const bg = settings.isPending
    ? -1
    : accountMode
      ? currentHex === null
        ? -1
        : CHOICES.findIndex((option) => option.hex.toUpperCase() === currentHex)
      : localBg;
  const selected = bg === -1 ? undefined : CHOICES[bg];
  /** 첫 조회가 끝나기 전과 저장이 나가 있는 동안 고르기를 잠근다. */
  const locked = settings.isPending || save.isPending;
  function setBg(i: number) {
    const option = CHOICES[i];
    if (!option || i === bg || locked) return;
    if (!accountMode) {
      setLocalBg(i);
      return;
    }
    // 서버가 받는 값만 보낸다(shared 팔레트). 거를 수 없으면 보내지 않는다.
    const hex = normalizeBackgroundColor(option.hex);
    if (hex === null) return;
    save.mutate(hex);
  }

  return (
    <>
      <PageHead frame="W-80" title="환경설정" subtitle="앱의 환경설정과 같은 내용입니다" />

      <div className="review" style={{ marginTop: 16 }}>
        <div className="editpanel">
          <section>
            <h3>사진 올리기</h3>
            {/* 고르는 자리는 오른쪽에 늘 펴 있다. 누를 곳이 없는 꺾쇠를 붙이지 않는다. */}
            <SettingRow label="기본 배경" value={selected?.label ?? ""} />
            <p className="hint">
              앱에 있는 <b>Wi-Fi에서만 올리기</b>와 <b>내보낸 파일 사진첩에 저장</b>은 여기
              없습니다.
            </p>
          </section>
        </div>

        <div className="editpanel">
          <section>
            {/* 제목은 앱 `A-40c` 시트 제목과 같다. 목업의 `. 열린 상태` 는 상태 표기라 옮기지 않는다. */}
            <h3>기본 배경</h3>
            <ScopeBanner>
              이 색은 <b>상세페이지 배경색</b>이지 화면 색이 아닙니다.
            </ScopeBanner>
            <div role="radiogroup" aria-label="기본 배경">
              {CHOICES.map((option, i) => (
                <button
                  type="button"
                  className={`optrow${bg === i ? " on" : ""}`}
                  key={option.key}
                  role="radio"
                  aria-checked={bg === i}
                  disabled={locked}
                  onClick={() => setBg(i)}
                >
                  <span className="rd" aria-hidden="true" />
                  <span className="sws" style={{ background: option.hex }} aria-hidden="true" />
                  <span>{option.label}</span>
                  <span className="ov mono">{option.hex}</span>
                </button>
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* ⭐ **상태 문장이라 남긴다.** 고를 수 있는데 아무 결과가 없으면 셀러가 바꾼 줄 안다.
          서버가 계정 설정을 쓰는지(`savesToAccount`)로 갈린다(design-manager 판정 2026-10-28, PRD `7.7.8a`).
          쓰지 않는 배포(운영, 처리방침 개정 시행일 전)에는 #245 이전 문장 그대로다. 그 배포에서는 새 작업이 이 값을
          읽지 않아 그 문장이 참이다. 시행일에 운영 스위치를 켜는 순간 문장이 함께 바뀐다(법무 `L-C38`-4).
          ⭐ 2026-10-31: 쓰는 배포에서는 작업 생성이 계정 값을 읽어 **새 작업이 이 배경으로 시작한다**
          (`apps/api/src/lib/jobs.ts` `readDefaultBackgroundForNewJob`). 켜짐 문장은 PRD `7.7.8a` 확정본이고 앱 `A-40b`
          켜짐 배너의 둘째 · 셋째 문장과 글자가 같다. `적용` 은 쓰지 않는다(이미 만든 작업까지 바뀌는 것처럼 읽힌다). */}
      {accountMode ? (
        <LimitBanner icon="i">새 작업은 이 배경으로 시작합니다. 이미 만든 작업은 바뀌지 않습니다.</LimitBanner>
      ) : (
        <LimitBanner icon="i">
          <b>고른 값이 아직 저장되지 않습니다.</b> 새로고침하면 처음 값으로 돌아갑니다. 상세페이지
          만들기가 아직 이 값을 읽지 않습니다.
        </LimitBanner>
      )}
    </>
  );
}
